// 🔧 engagement.js
// ✅ Purpose: Manager dashboard for Engagement Engine and Event Intelligence
// ✅ Features:
// - Shows manager-facing Engagement Intelligence foundation
// - Requires the Passport add-on to be enabled
// - Loads recent engagement_events for the current event
// - Shows live-style timeline of Passport and Engagement interactions
// - Displays summary cards for participants, points, sponsor interactions, and vendor interactions
// - Uses AddonRequired when the add-on has not been purchased
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";

import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";

const EVENT_ICONS = {
  passport_viewed: "🧭",
  passport_stop_viewed: "📍",
  activity_completed: "🏆",
  points_awarded: "⭐",
  raffle_entry_awarded: "🎟️",
  sponsor_qr_scanned: "🤝",
  vendor_qr_scanned: "🛍️",
  passport_completed: "🎉",
  achievement_unlocked: "🏅",
};

const formatEventLabel = (eventType = "") => {
  return String(eventType || "engagement_event")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDateTime = (value) => {
  if (!value) return "Recently";

  const date =
    typeof value.toDate === "function"
      ? value.toDate()
      : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Recently";
  }

  return date.toLocaleString();
};

const sumField = (rows, fieldName) => {
  return rows.reduce((total, row) => {
    return total + Number(row[fieldName] || 0);
  }, 0);
};

const Engagement = () => {
  const {
    eventslug,
    eventId,
    eventDoc,
    ready,
    eventLoading,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const passportEnabled = hasPurchasedAddOn(eventDoc, "passport");

  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dailyRows, setDailyRows] = useState([]);
  const [topStops, setTopStops] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const engagementEventsRef = useMemo(() => {
    if (!eventId) return null;

    return collection(db, "events", eventId, "engagement_events");
  }, [eventId]);

  const loadEngagementTimeline = async () => {
    if (!engagementEventsRef || !passportEnabled) {
      setEvents([]);
      setLoadingEvents(false);
      return;
    }

    try {
      setLoadingEvents(true);

      const snap = await getDocs(
        query(
          engagementEventsRef,
          orderBy("occurredAt", "desc"),
          limit(50)
        )
      );

      const rows = snap.docs.map((eventDocSnap) => ({
        id: eventDocSnap.id,
        ...eventDocSnap.data(),
      }));

      setEvents(rows);
    } catch (err) {
      logger.error("❌ Failed to load engagement timeline:", {
        value: err,
      });
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEngagementSummary = async () => {
    if (!eventId || !passportEnabled) {
      setSummary(null);
      setDailyRows([]);
      return;
    }

    try {
      const [summarySnap, dailySummarySnap] = await Promise.all([
        getDocs(collection(db, "events", eventId, "engagement_summary")),
        getDocs(
          query(
            collection(db, "events", eventId, "engagement_daily_summary"),
            orderBy("date", "desc")
          )
        ),
      ]);

      const mainSummary = summarySnap.docs.find((docSnap) => {
        return docSnap.id === "main";
      });

      setSummary(mainSummary?.data() || null);
      setDailyRows(
        dailySummarySnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    } catch (err) {
      logger.error("❌ Failed to load engagement summary:", {
        value: err,
      });
    }
  };

  const loadTopStops = async () => {
    if (!eventId || !passportEnabled) {
      setTopStops([]);
      return;
    }

    try {
      const snap = await getDocs(
        query(
          collection(db, "events", eventId, "engagement_activity_summary"),
          orderBy("totalCompletions", "desc"),
          limit(5)
        )
      );

      setTopStops(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    } catch (err) {
      logger.error("❌ Failed to load top Passport stops:", {
        value: err,
      });
    }
  };

  const uniqueParticipantCount = useMemo(() => {
    return Number(summary?.totalParticipants || 0);
  }, [summary]);

  const stopCollectedCount = useMemo(() => {
    return Number(summary?.totalTransactions || 0);
  }, [summary]);

  const passportCompletedCount = useMemo(() => {
    return sumField(dailyRows, "completedPassports");
  }, [dailyRows]);

  const completionRate = useMemo(() => {
    if (uniqueParticipantCount === 0) return 0;

    return Math.round(
      (passportCompletedCount / uniqueParticipantCount) * 100
    );
  }, [passportCompletedCount, uniqueParticipantCount]);

  useEffect(() => {
    if (!ready || eventLoading) return;

    loadEngagementTimeline();
    loadEngagementSummary();
    loadTopStops();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventLoading, eventId, passportEnabled]);

  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading Engagement Dashboard..." />;
  }

  if (eventDoc && !passportEnabled) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Engagement Passport Add-on Required"
        description="Purchase the Engagement Passport add-on before using Event Intelligence, activity tracking, points, and engagement analytics."
      />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="display-5 mb-2">
              🧭 Engagement Dashboard
            </h1>

            <p className="lead text-muted mb-0">
              Event Intelligence powered by Passport Stops, attendee actions, and Engagement Analytics.
            </p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Participants
                </div>

                <div className="display-6 fw-bold">
                  {uniqueParticipantCount}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Stops Collected
                </div>

                <div className="display-6 fw-bold">
                  {stopCollectedCount}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Passports Completed
                </div>

                <div className="display-6 fw-bold">
                  {passportCompletedCount}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Completion Rate
                </div>

                <div className="display-6 fw-bold">
                  {completionRate}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white">
            <strong>Top Passport Stops</strong>
          </div>

          <div className="card-body">
            {topStops.length === 0 ? (
              <div className="alert alert-info mb-0">
                No collected stops yet. Once attendees redeem Passport codes,
                the most active stops will appear here.
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {topStops.map((stop, index) => (
                  <div
                    key={stop.activityId}
                    className="border rounded p-3 bg-white"
                  >
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <div>
                        <div className="fw-bold">
                          #{index + 1} {stop.activityTitle}
                        </div>

                        <div className="small text-muted">
                          {stop.totalPointsAwarded || 0} points awarded
                        </div>
                      </div>

                      <span className="badge bg-success">
                        {stop.totalCompletions || 0} collected
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <strong>Engagement Timeline</strong>

            <span className="badge bg-secondary">
              Last {events.length}
            </span>
          </div>

          <div className="card-body">
            {loadingEvents ? (
              <div className="text-muted">
                Loading engagement timeline...
              </div>
            ) : events.length === 0 ? (
              <div className="alert alert-info mb-0">
                No engagement events have been recorded yet. Open the Passport, view a stop,
                or redeem a code to see this timeline come alive.
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {events.map((event) => {
                  const metadata = event.metadata || {};
                  const icon = EVENT_ICONS[event.eventType] || "📌";

                  return (
                    <div
                      key={event.id}
                      className="border rounded p-3 bg-white"
                    >
                      <div className="d-flex justify-content-between gap-3">
                        <div>
                          <div className="fw-bold">
                            {icon} {formatEventLabel(event.eventType)}
                          </div>

                          <div className="small text-muted">
                            {metadata.activityTitle ||
                              metadata.page ||
                              event.source ||
                              "Engagement"}
                          </div>
                        </div>

                        <div className="text-end small text-muted">
                          {formatDateTime(event.occurredAt || event.createdAt)}
                        </div>
                      </div>

                      <div className="row g-2 mt-2 small">
                        {metadata.activityType && (
                          <div className="col-12 col-md-4">
                            <strong>Type:</strong>{" "}
                            {metadata.activityType}
                          </div>
                        )}

                        {metadata.sourceModule && (
                          <div className="col-12 col-md-4">
                            <strong>Module:</strong>{" "}
                            {metadata.sourceModule}
                          </div>
                        )}

                        {metadata.pointsAwarded !== undefined && (
                          <div className="col-12 col-md-4">
                            <strong>Points:</strong>{" "}
                            {metadata.pointsAwarded}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="alert alert-light border mt-4 mb-0">
          <strong>Foundation:</strong> This timeline is the first customer-facing view
          of the Engagement Intelligence Engine. Future versions will summarize sponsor
          ROI, vendor engagement, Passport completion, activity performance, and attendee journeys.
        </div>
      </div>
    </section>
  );
};

export default Engagement;
